import React, { useEffect } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import useChildEditor from '../../../hooks/use-child-editor';
import {
  costPriceValidation,
  vatRateValidation,
  requiredNameWithMaxLength,
  requiredOptionsPrice,
  requiredStatus,
  requiredType,
} from '../../../utils/validation';
import AddButton from '../../shared/AddButton';
import Alert from '../../shared/Alert';
import Checkbox from '../../shared/Checkbox';
import EditorForm from '../../shared/EditorForm';
import EditorRow from '../../shared/EditorRow';
import LimitSelect from '../../shared/LimitSelect';
import NameField from '../../shared/NameField';
import OptionsField from '../../shared/OptionsField';
import VariantTypes from '../../shared/VariantTypes';

const validation = Yup.object({
  name: requiredNameWithMaxLength,
  status: requiredStatus,
  type: requiredType,
  children: Yup.array().of(
    Yup.object().shape({
      price: requiredOptionsPrice,
      cost_price: costPriceValidation,
      vat_rate_eat_in: vatRateValidation,
      vat_rate_takeaway: vatRateValidation,
      name: requiredNameWithMaxLength,
    })
  ),
});
const Border = styled.div`
  height: 2px;
  width: 104%;
  border-bottom: 1px solid #dedede;
  position: relative;
  right: 16px;
  margin-bottom: 30px;
  @media only screen and (device-width: 768px) and (device-height: 1024px) {
    width: 111%;
    right: 30px;
  }
  @media only screen and (device-width: 820px) and (device-height: 1180px) {
    width: 110%;
    right: 30px;
  }
`;
const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 815px;
  width: 100%;
  margin: 0px;
  background: #f3f4f6;
  box-sizing: border-box;
  border-radius: 5px;
  @media only screen and (device-width: 768px) and (device-height: 1024px) {
    width: 610px;
  }
  @media only screen and (device-width: 820px) and (device-height: 1180px) {
    width: 660px;
  }
  @media only screen and (device-width: 912px) and (device-height: 1224px) {
    width: 755px;
  }
`;

const OptionsFieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const AddButtonContainer = styled.div`
  margin: 0px 0 0;
  padding: 15px 0 0 0;
`;

const LimitSelectResized = styled(LimitSelect)`
  width: fit-content;
  margin-right: 10px;
  display: inline-block;
`;

const FlexEditorRow = styled(EditorRow)`
  display: flex;
  align-items: flex-end;
`;

const RequiredCheckbox = styled(Checkbox)`
  width: fit-content;
  margin-bottom: 0px;
  display: flex;
  height: 35px;
`;

function OptionGroupEditor(props) {
  const {
    variant,
    error,
    onChange,
    onRemove,
    onEditorSetup,
    onParentFieldChange,
    createMode,
    onAdd,
  } = props;

  const { item, onAddOption, onChangeOption, onFieldChange, onRemoveOption } =
    useChildEditor({
      init: variant,
      validation,
      onEditorSetup,
      onChange,
    });
  const onFieldChangeFn = createMode ? onFieldChange : onParentFieldChange;

  const onRemoveFn = createMode ? onRemoveOption : onRemove;

  const onChangeFn = createMode ? onChangeOption : onChange;

  const onAddFn = createMode ? onAddOption : onAdd;

  const onNameChange = onFieldChangeFn('name');
  const onLimitChange = onFieldChangeFn('choice_limit');
  const onRequiredChange = onFieldChangeFn('required');
  const handleAddOption = () =>
    onAddFn({
      name: '',
      variantType: 'single',
      choice_limit: 0,
      price: 0,
      cost_price: null,
      vat_rate_eat_in: null,
      vat_rate_takeaway: null,
      type: 'option',
      status: 'live',
    });

  const showLimit = item.variantType === 'multiple';

  const onVariantTypeChange = e => {
    if (createMode) {
      if (showLimit && e.target.value === 'single') {
        // reset choice_limit on transition to single variantType
        onChange({
          ...item,
          variantType: 'single',
          choice_limit: 0,
          children: (item.children || []).map(option => {
            return { ...option, choice_limit: 0 };
          }),
        });
      } else {
        onFieldChangeFn('variantType')(e);
      }
    } else {
      onFieldChangeFn('variantType')(e);
    }
  };

  useEffect(() => {
    if ((item.children || []).length < 1) {
      handleAddOption();
    }
  }, []);

  const handleRequiredChanged = (event, checked) => {
    onRequiredChange({ target: { value: checked } });
  };

  return (
    <EditorContainer>
      <EditorForm>
        <NameField
          id={`variant-name-${item.id}`}
          type="variant"
          label="Title"
          value={item.name || ''}
          placeholder="e.g. Mixers, Extra Toppings or Sides"
          onChange={onNameChange}
        />

        <FlexEditorRow>
          <VariantTypes
            id={`variant-type-select-${item.id}`}
            value={item.variantType}
            onChange={onVariantTypeChange}
          />
          {showLimit ? (
            <LimitSelectResized
              id={`limit-${item.id}`}
              onChange={onLimitChange}
              value={item.choice_limit}
            />
          ) : null}

          <RequiredCheckbox
            onSelect={handleRequiredChanged}
            value="Required"
            id={item.id}
            key={`item-required-${item.id}`}
            isChecked={item.required}
          />
        </FlexEditorRow>
        <Border />

        <OptionsFieldsContainer>
          {item.children.map((option, idx) => (
            <OptionsField
              key={`option-${option.id}`}
              id={`option-${option.id}`}
              option={option}
              hideLabel={idx !== 0}
              disableDelete={idx === 0}
              showLimit={showLimit}
              onChange={onChangeFn}
              onDelete={() => onRemoveFn(option)}
            />
          ))}
        </OptionsFieldsContainer>

        <AddButtonContainer>
          <AddButton onClick={handleAddOption}>
            <span style={{ fontSize: '13px' }}>Add another choice</span>
          </AddButton>
        </AddButtonContainer>
      </EditorForm>
      {error ? <Alert>{error}</Alert> : null}
    </EditorContainer>
  );
}

export default OptionGroupEditor;



// WEBPACK FOOTER //
// ./src/components/Dashboard/Panels/MenuEditor/components/GlobalOptions/components/option-groups-editor.jsx